/** SettingsMeetingConnections — multi-platform binding management.
 *
 * One card per supported platform (Tencent Meeting, Dingtalk). Upcoming
 * platforms (Feishu, Zoom) render as disabled "敬请期待" cards so the user
 * sees the full picture. Bindings are per-user — each lawyer connects the
 * platforms their own clients actually use.
 *
 * Tencent uses the device-auth channel (CLI 同款，免应用注册)：点击后系统生成
 * 授权链接，用户在新窗口完成腾讯会议授权，前端轮询连接状态直到 active。其他
 * 平台维持整页跳转的 OAuth 流程。 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Space, Tag, Typography, message } from "antd";
import { CopyOutlined, DingdingOutlined, LinkOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { meetingsApi } from "../../api";
import { useCurrentUser } from "../../api/queries";
import {
  EXTERNAL_MEETING_SOURCES,
  MEETING_SOURCE_LABELS,
  type MeetingConnection,
  type MeetingSource,
} from "../../types/meeting";

const { Text, Paragraph } = Typography;

const QK = ["meeting-connections"] as const;

/** 设备授权轮询：2s 间隔，最长 5 分钟（与后端授权窗口对齐）。 */
const DEVICE_AUTH_POLL_INTERVAL_MS = 2_000;
const DEVICE_AUTH_POLL_MAX_TICKS = 150;

/** Platforms we render cards for in display order. Includes upcoming ones. */
const ALL_PLATFORM_CARDS: {
  source: MeetingSource;
  icon: React.ReactNode;
  description: string;
  upcoming?: boolean;
  /** device = 设备授权通道（生成链接 + 轮询）；oauth = 整页跳转。 */
  authFlow?: "device" | "oauth";
}[] = [
  {
    source: "tencent",
    icon: <VideoCameraOutlined />,
    description: "国内主流会议平台，企业客户常用。",
    authFlow: "device",
  },
  {
    source: "dingtalk",
    icon: <DingdingOutlined />,
    description: "钉钉生态企业客户首选，会议+IM 一体化。",
  },
  { source: "feishu", icon: <DingdingOutlined />, description: "飞书会议（妙记转写）。", upcoming: true },
  { source: "zoom", icon: <VideoCameraOutlined />, description: "跨境仲裁、海外客户常用。", upcoming: true },
];

function statusTag(status: MeetingConnection["status"] | undefined) {
  if (status === "pending") return <Tag color="processing">等待授权</Tag>;
  if (status === "active") return <Tag color="green">已连接</Tag>;
  if (status === "expired") return <Tag color="orange">授权过期</Tag>;
  if (status === "revoked") return <Tag color="red">已撤销</Tag>;
  return <Tag>未连接</Tag>;
}

export default function SettingsMeetingConnections() {
  const qc = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const [messageApi, contextHolder] = message.useMessage();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: QK,
    queryFn: () => meetingsApi.listConnections(),
  });

  // 设备授权进行中的 source → 授权链接（打开新窗口后轮询连接状态）。
  const [deviceAuth, setDeviceAuth] = useState<{
    source: MeetingSource;
    url: string;
  } | null>(null);
  const pollTickRef = useRef(0);

  const connectionsBySource = new Map<string, MeetingConnection>(
    (data?.data ?? []).map((c) => [c.source, c]),
  );

  const stopDevicePolling = useCallback(
    (invalidate = true) => {
      setDeviceAuth(null);
      pollTickRef.current = 0;
      if (invalidate) void qc.invalidateQueries({ queryKey: QK });
    },
    [qc],
  );

  // 轮询连接状态：active → 成功提示并停止；revoked/超时 → 失败提示并停止。
  useEffect(() => {
    if (!deviceAuth) return;
    const timer = window.setInterval(async () => {
      pollTickRef.current += 1;
      if (pollTickRef.current > DEVICE_AUTH_POLL_MAX_TICKS) {
        stopDevicePolling();
        messageApi.error("授权超时（5 分钟窗口），请重新发起授权");
        return;
      }
      try {
        const res = await meetingsApi.listConnections();
        const conn = (res.data ?? []).find((c) => c.source === deviceAuth.source);
        if (conn?.status === "active") {
          messageApi.success(`${MEETING_SOURCE_LABELS[deviceAuth.source]} 授权成功`);
          stopDevicePolling();
        } else if (conn?.status === "revoked" || conn?.status === "expired") {
          stopDevicePolling();
          messageApi.error("授权未完成或已失效，请重新发起");
        }
      } catch {
        // 单次轮询失败不打断流程，等下一个 tick。
      }
    }, DEVICE_AUTH_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [deviceAuth, messageApi, stopDevicePolling]);

  const beginDeviceAuth = useMutation({
    mutationFn: (source: MeetingSource) => meetingsApi.startDeviceAuth(source),
    onSuccess: (res, source) => {
      const url = res.data?.authorizeUrl;
      if (!url) {
        messageApi.error(`未能生成 ${MEETING_SOURCE_LABELS[source]} 授权链接`);
        return;
      }
      window.open(url, "_blank", "noopener");
      setDeviceAuth({ source, url });
      pollTickRef.current = 0;
      messageApi.info("已打开授权页面，请在浏览器中完成腾讯会议授权");
    },
    onError: (_err, source) =>
      messageApi.error(`无法发起 ${MEETING_SOURCE_LABELS[source]} 授权，请稍后重试`),
  });

  const beginOAuth = useMutation({
    mutationFn: (source: MeetingSource) => meetingsApi.beginOAuth(source),
    onSuccess: (res, source) => {
      const url = res.data?.authorizeUrl;
      if (!url) {
        messageApi.error(`未能获取 ${source} 授权链接`);
        return;
      }
      window.location.href = url;
    },
    onError: (_err, source) => messageApi.error(`无法发起 ${source} 授权，请稍后重试`),
  });

  const disconnect = useMutation({
    mutationFn: (source: MeetingSource) => meetingsApi.disconnect(source),
    onSuccess: (_res, source) => {
      messageApi.success(`已解除 ${MEETING_SOURCE_LABELS[source]} 绑定`);
      qc.invalidateQueries({ queryKey: QK });
    },
    onError: (_err, source) => messageApi.error(`解绑 ${MEETING_SOURCE_LABELS[source]} 失败`),
  });

  const startAuth = (source: MeetingSource, authFlow: "device" | "oauth" | undefined) => {
    if (authFlow === "device") beginDeviceAuth.mutate(source);
    else beginOAuth.mutate(source);
  };

  // ?oauth=ok / ?oauth=error after the OAuth callback redirect
  const oauthResult =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("oauth")
      : null;

  return (
    <div>
      {contextHolder}
      <h2 style={{ marginTop: 0 }}>会议平台</h2>
      <Paragraph type="secondary" style={{ marginBottom: "var(--jerry-sp-4)" }}>
        绑定你实际使用的会议平台。一位律师的客户可能分布在腾讯会议、钉钉、飞书
        等不同平台——绑定多个平台后，会议助手会一次性聚合所有平台的会议。
        每个绑定都是你专属的，与其他律师隔离。
      </Paragraph>

      {deviceAuth && (
        <Alert
          type="info"
          showIcon
          message={`正在等待 ${MEETING_SOURCE_LABELS[deviceAuth.source]} 授权确认…`}
          description={
            <Space direction="vertical" size="small" style={{ width: "100%" }} data-testid="device-auth-pending-alert">
              <Text style={{ fontSize: "var(--jerry-font-size-sm)" }}>
                请在刚打开的浏览器窗口中完成授权。没有弹出窗口？点击下方链接重新打开（5 分钟内有效）：
              </Text>
              <Space>
                <a href={deviceAuth.url} target="_blank" rel="noopener noreferrer">
                  打开授权页面
                </a>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    void navigator.clipboard?.writeText(deviceAuth.url);
                    messageApi.success("授权链接已复制");
                  }}
                >
                  复制链接
                </Button>
                <Button size="small" onClick={() => stopDevicePolling(false)}>
                  取消等待
                </Button>
              </Space>
            </Space>
          }
          style={{ marginBottom: "var(--jerry-sp-3)" }}
        />
      )}

      {oauthResult === "ok" && (
        <Alert
          type="success"
          message="会议平台授权成功"
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          closable
        />
      )}
      {oauthResult === "error" && (
        <Alert
          type="error"
          message="授权失败，请重试或联系管理员"
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          closable
        />
      )}
      {isError && (
        <Alert
          type="error"
          message="无法读取已绑定状态，请刷新页面重试"
          style={{ marginBottom: "var(--jerry-sp-3)" }}
          action={<Button size="small" onClick={() => refetch()}>重试</Button>}
        />
      )}

      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        {ALL_PLATFORM_CARDS.map(({ source, icon, description, upcoming, authFlow }) => {
          const conn = connectionsBySource.get(source);
          const isBound = !!conn;
          const authPending = deviceAuth?.source === source || conn?.status === "pending";
          const primaryLabel = isBound ? "重新授权" : "前往授权";
          return (
            <Card
              key={source}
              size="small"
              styles={{ body: { padding: "var(--jerry-sp-4) var(--jerry-sp-5)" } }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "var(--jerry-sp-3)",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "var(--jerry-sp-3)", minWidth: 0 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: "var(--jerry-gray-100)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--jerry-primary-500)",
                      fontSize: 20,
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--jerry-sp-2)" }}>
                      <Text strong>{MEETING_SOURCE_LABELS[source]}</Text>
                      {upcoming ? (
                        <Tag>即将推出</Tag>
                      ) : (
                        statusTag(conn?.status)
                      )}
                    </div>
                    <Text type="secondary" style={{ fontSize: "var(--jerry-font-size-sm)" }}>
                      {description}
                    </Text>
                    {conn?.expiresAt && conn.status === "active" && (
                      <div>
                        <Text
                          type="secondary"
                          style={{ fontSize: "var(--jerry-font-size-xs)" }}
                        >
                          授权至 {new Date(conn.expiresAt).toLocaleString("zh-CN")}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
                <Space>
                  {upcoming ? (
                    <Button disabled>敬请期待</Button>
                  ) : (
                    <>
                      <Button
                        type={isBound ? "default" : "primary"}
                        icon={<LinkOutlined />}
                        loading={beginDeviceAuth.isPending || beginOAuth.isPending}
                        disabled={authPending}
                        data-testid={`meeting-connect-${source}`}
                        onClick={() => startAuth(source, authFlow)}
                      >
                        {authPending ? "等待授权中…" : primaryLabel}
                      </Button>
                      {isBound && (
                        <Button
                          danger
                          loading={disconnect.isPending}
                          onClick={() => disconnect.mutate(source)}
                        >
                          解除绑定
                        </Button>
                      )}
                    </>
                  )}
                </Space>
              </div>
            </Card>
          );
        })}
      </Space>

      {!isLoading && (data?.data ?? []).length > 0 && (
        <Paragraph
          type="secondary"
          style={{
            marginTop: "var(--jerry-sp-4)",
            fontSize: "var(--jerry-font-size-sm)",
          }}
        >
          已绑定 {(data?.data ?? []).length} 个平台。回到「会议助手」即可一键同步全部会议。
        </Paragraph>
      )}
    </div>
  );
}
